import moment from "moment";

const formatDayMonthToMoment = (day, month, year) => {
    if(year) return formatDayMonthYearToMoment(day, month, year);
    return moment(new Date().getFullYear() + "-" + month + "-" + day, "YYYY-MM-DD");
}

const formatDayMonthYearToMoment = (day, month, year) => {
    return moment(year + "-" + month + "-" + day, "YYYY-MM-DD");
}


const calcFirstAvailabilityBasedOnUnavailabilities = (room) => {
    if(!room) return null;
    let firstAvailability = moment(room.availableFrom);
    if(firstAvailability.isBefore(moment()) || !firstAvailability.isValid()) firstAvailability = moment();
    [...(room.unavailability||[]), ...(room.importedUnavailability||[])]
        .filter(unavailability => {
            if(!unavailability || unavailability.removed) return false;
            let momentEnd = moment(unavailability.end);
            return momentEnd.isAfter(firstAvailability) || momentEnd.isSame(firstAvailability);
        }).sort((u1,u2) => {
        let d1 = new Date(u1.end), d2 = new Date(u2.end);
        return d1 > d2 ? 1 : d1 < d2 ? -1 : 0;
    }).forEach(unavailability => {
        //a gap shorter than the minimum stay before the unavailability is not bookable either (same as the backend)
        let startMoment = moment(unavailability.start).subtract(room.periodStayMin, "month");
        let endMoment = moment(unavailability.end);
        if((firstAvailability.isAfter(startMoment) || firstAvailability.isSame(startMoment)) &&
            (firstAvailability.isBefore(endMoment) || firstAvailability.isSame(endMoment))) {
            firstAvailability = endMoment.clone().add(1, "day");
        }
    });
    return firstAvailability
}

const calcFirstAvailability = (room) => {
    const now = moment();
    if(!room) return now;
    let firstAvailabilityBasedOnUnavailabilities = calcFirstAvailabilityBasedOnUnavailabilities(room);
    if(!room.hasAvailability) return firstAvailabilityBasedOnUnavailabilities;
    //calcFirstAvailabilityBasedOnUnavailabilities already seeds from room.availableFrom, clamps it
    //up to now and only ever moves forward, so it is the right base on its own (mirrors the backend utils.calcFirstAvailability)
    let firstAvailability = firstAvailabilityBasedOnUnavailabilities.clone();

    let availability_periods = room.availability_periods || [];

    availability_periods = availability_periods.sort((slot1, slot2) => {
        const currentYear = firstAvailability.year();
        const slot1moveInStart = new Date(slot1.temporary_year ? (slot1.temporary_year - 1) : currentYear, slot1.move_in.start.month - 1, slot1.move_in.start.day);
        const slot2moveInStart = new Date(slot2.temporary_year ? (slot2.temporary_year - 1) : currentYear, slot2.move_in.start.month - 1, slot2.move_in.start.day);
        let slot1moveInEnd = new Date(slot1moveInStart.getFullYear(), slot1.move_in.end.month - 1, slot1.move_in.end.day);
        let slot2moveInEnd = new Date(slot2moveInStart.getFullYear(), slot2.move_in.end.month - 1, slot2.move_in.end.day);
        while(slot1moveInEnd < slot1moveInStart) slot1moveInEnd.setFullYear(slot1moveInEnd.getFullYear() + 1)
        while(slot2moveInEnd < slot2moveInStart) slot2moveInEnd.setFullYear(slot2moveInEnd.getFullYear() + 1)
        let betweenSlot1 = firstAvailability.isBetween(slot1moveInStart, slot1moveInEnd, null, "[]");
        let betweenSlot2 = firstAvailability.isBetween(slot2moveInStart, slot2moveInEnd, null, "[]");
        if (betweenSlot1 && !betweenSlot2) {
            return -1;
        } else if (!betweenSlot1 && betweenSlot2) {
            return 1;
        } else if (betweenSlot1 && betweenSlot2) {
            return slot1moveInEnd - slot2moveInEnd;
        }
        while (moment(slot1moveInStart).isBefore(firstAvailability)) slot1moveInStart.setFullYear(slot1moveInStart.getFullYear() + 1);
        while (moment(slot2moveInStart).isBefore(firstAvailability)) slot2moveInStart.setFullYear(slot2moveInStart.getFullYear() + 1);
        return slot1moveInStart - slot2moveInStart;
    });

    // If there are no availability periods, return the first availability based on unavailabilities
    if (availability_periods.length < 1 || room.hasAvailability === false) {
        return firstAvailabilityBasedOnUnavailabilities;
    }

    const unavailabilities = [...(room.unavailability || []), ...(room.importedUnavailability || [])]
        .filter(unavailability => !unavailability.removed && moment(unavailability.end).isAfter(now))
        .sort((u1, u2) => moment(u1.end) - moment(u2.end));

    // Find the earliest valid availability slot: the earliest valid move-in of every period of the year, plus the year
    // after the first hit, not the first valid period in list order (same as the backend)
    let earliestValidAvailability = null;
    let earliestValidYear = null;
    let curYear = firstAvailability.year();
    //prevent infinite loop, max 10 years
    while((earliestValidYear === null || curYear <= earliestValidYear + 1) && curYear < firstAvailability.year() + 10) {
        for (const availability of availability_periods) {
            //a temporary period only exists in its own year (temporary_year comes as a string from the form)
            if (availability.temporary_year && Number(availability.temporary_year) !== curYear) continue;
            let moveInStart = formatDayMonthToMoment(availability.move_in.start.day, availability.move_in.start.month, curYear);
            let moveInEnd = formatDayMonthToMoment(availability.move_in.end.day, availability.move_in.end.month, curYear);

            if (moveInEnd.isBefore(moveInStart)) {
                moveInEnd.add(1, "year");
                //A move-in window crossing New Year (e.g. 15/11 -> 28/02) also has an occurrence that opened the
                //previous year and may still be open. Prefer it while it has not closed (same as the backend)
                let previousMoveInStart = moveInStart.clone().subtract(1, "year");
                let previousMoveInEnd = moveInEnd.clone().subtract(1, "year");
                if (!previousMoveInEnd.isBefore(firstAvailability, "day")) {
                    moveInStart = previousMoveInStart;
                    moveInEnd = previousMoveInEnd;
                }
            }

            if (moveInEnd.isBefore(firstAvailability, "day")) {
                continue; // is before first availability
            }
            let moveOutStart = formatDayMonthToMoment(availability.move_out.start.day, availability.move_out.start.month, curYear);
            let moveOutEnd = formatDayMonthToMoment(availability.move_out.end.day, availability.move_out.end.month, curYear);

            if (moveOutStart.isBefore(moveInEnd, "day")) {
                moveOutStart.add(1, "year");
                moveOutEnd.add(1, "year");
            }

            if (moveInStart.isBefore(firstAvailability, "day")) {
                moveInStart = firstAvailability.clone();
            }

            //by day: an unavailability's last day is still unavailable (same as the backend)
            if (unavailabilities.some(u => {
                const uStart = moment(u.start).startOf("day");
                const uEnd = moment(u.end).startOf("day");
                return (uStart.isSameOrAfter(moveInStart, "day") && uEnd.isSameOrBefore(moveOutEnd, "day")) ||
                    (uStart.isBefore(moveInStart, "day") && uEnd.isSameOrAfter(moveInEnd, "day")) ||
                    (uStart.isBefore(moveOutStart, "day") && uEnd.isAfter(moveOutEnd, "day"));
            })) {
                continue; // overlaps with an unavailability
            }

            let earliestSemiOverlap = null;
            for(const unavailability of unavailabilities) {
                const uStart = moment(unavailability.start).startOf("day");
                const uEnd = moment(unavailability.end).startOf("day");
                if(uStart.isBefore(moveInStart, "day") && uEnd.isBefore(moveInEnd, "day") && uEnd.isSameOrAfter(moveInStart, "day")) { //unavailability ends before the last move in day on this availability
                    earliestSemiOverlap = uEnd.clone().add(1, "day");
                }
            }

            let validAvailability = earliestSemiOverlap || moveInStart;
            if (!earliestValidAvailability || validAvailability.isBefore(earliestValidAvailability)) {
                earliestValidAvailability = validAvailability;
            }
            if (earliestValidYear === null) earliestValidYear = curYear;
        }
        curYear++;
    }

    if (earliestValidAvailability === null) {
        firstAvailability = calcFirstAvailabilityBasedOnUnavailabilities(room);
        for (const availability of availability_periods) {
            let start = formatDayMonthYearToMoment(availability.move_in.start.day, availability.move_in.start.month, firstAvailability.year());
            if (start.isSameOrAfter(earliestValidAvailability, "day")) {
                earliestValidAvailability = start;
                break;
            }
        }
        if (earliestValidAvailability === null) earliestValidAvailability = formatDayMonthYearToMoment(availability_periods[0].move_in.start.day, availability_periods[0].move_in.start.month, firstAvailability.year() + 1);
    }

    return earliestValidAvailability;
}

export {calcFirstAvailability};
